import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import fileService from '../services/fileService';
import PropTypes from 'prop-types';
import logger from '../utils/logger';
import '../styles/profile-edit-modal.css';
import '../styles/modal.css';

/**
 * Modal de edición de perfil específico para tutores
 * Solo muestra campos relevantes para tutores (información personal y redes sociales)
 */
const TutorProfileEditModal = ({ isOpen, onClose, onSaved }) => {
  const { user, updateUser } = useAuth();
  const [formData, setFormData] = useState({
    // Información básica del tutor
    name: '',
    email: '',
    phone: '',
    bio: '',
    birthdate: '',
    address: '',
    city: '',
    postalCode: '',
    country: '',
    // Redes sociales del tutor
    personalInstagram: '',
  });
  const [avatar, setAvatar] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const fileInputRef = useRef(null);
  const modalRef = useRef(null);

  // Cargar datos del usuario cuando se abre el modal
  useEffect(() => {
    if (isOpen && user) {
      const formattedBirthdate = user.birthdate 
        ? new Date(user.birthdate).toISOString().split('T')[0] 
        : '';

      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        bio: user.bio || '',
        birthdate: formattedBirthdate,
        address: (user.address && user.address.street) || '',
        city: (user.address && user.address.city) || '',
        postalCode: (user.address && user.address.postalCode) || '',
        country: (user.address && user.address.country) || 'España',
        personalInstagram: user.personalInstagram || user.extendedProfile?.familyInstagram || '',
      });

      setAvatarPreview(user.avatar || null); // null para mostrar avatar por defecto
      setErrors({});
      setSuccess(false);
    }
  }, [isOpen, user]);

  // Cerrar el modal al hacer clic fuera de él
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatar(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setAvatarPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Componente de avatar por defecto simple
  const DefaultAvatar = () => (
    <div
      style={{
        width: '100%',
        height: '100%',
        borderRadius: '50%',
        backgroundColor: '#ff0000',
        border: '3px solid #000000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#ffffff',
        fontSize: '24px',
        fontWeight: 'bold',
        fontFamily: 'Arial, sans-serif'
      }}
    >
      AVATAR
    </div>
  );

  const validateForm = () => {
    const newErrors = {};

    // Validaciones básicas
    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'El email no es válido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      // Subir avatar si hay uno nuevo
      let avatarUrl = user.avatar;
      if (avatar) {
        try {
          avatarUrl = await fileService.uploadFile(avatar, 'avatars');
        } catch (error) {
          console.error('Error al subir avatar:', error);
          logger.error('Error al subir avatar', { error });
        }
      }

      const {
        name, email, phone, bio, birthdate, address, city, postalCode, country,
        personalInstagram
      } = formData;

      // Preparar datos del perfil
      const profileData = {
        name,
        email,
        phone,
        bio,
        birthdate: birthdate || null,
        address: {
          street: address,
          city,
          postalCode,
          country
        },
        personalInstagram,
        avatar: avatarUrl,
      };

      // Actualizar perfil
      await updateUser(profileData);

      setSuccess(true);
      setTimeout(() => {
        handleClose();
        if (onSaved) {
          onSaved();
        }
      }, 1500);

    } catch (error) {
      console.error('Error al actualizar perfil:', error);
      logger.error('Error al actualizar perfil', { error });
      setErrors({ submit: 'Error al actualizar el perfil. Inténtalo de nuevo.' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: '',
        email: '',
        phone: '',
        bio: '',
        birthdate: '',
        address: '',
        city: '',
        postalCode: '',
        country: '',
        personalInstagram: '',
      });
      setAvatar(null);
      setAvatarPreview('');
      setErrors({});
      setSuccess(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container profile-edit-modal" ref={modalRef}>
        <div className="modal-header" style={{backgroundColor: '#ff0000', color: 'white'}}>
          <h2>🔥🔥🔥 MODAL TUTOR ESPECÍFICO 🔥🔥🔥</h2>
          <button
            className="modal-close"
            onClick={handleClose}
            disabled={loading}
          >
            ×
          </button>
        </div>

        {success && (
          <div className="success-message">
            <span className="material-icons">check_circle</span>
            <span>Perfil actualizado correctamente</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="modal-body">
          <div className="profile-form">
            
            {/* Sección de Avatar */}
            <div className="profile-section">
              <h3 className="section-title">
                <span className="material-icons">account_circle</span>
                Foto de Perfil
              </h3>
              
              <div className="avatar-upload">
                <div className="avatar-preview">
                  {(avatarPreview && avatarPreview.startsWith('data:image/')) ? (
                    <img
                      src={avatarPreview}
                      alt="Avatar"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : (
                    <DefaultAvatar />
                  )}
                  <div style={{ display: 'none' }}>
                    <DefaultAvatar />
                  </div>
                </div>
                <div className="avatar-controls">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleAvatarChange}
                    accept="image/*"
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Cambiar Foto
                  </button>
                </div>
              </div>
            </div>

            {/* Sección de Información Personal */}
            <div className="profile-section">
              <h3 className="section-title">
                <span className="material-icons">person</span>
                Información Personal
              </h3>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Nombre completo *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`form-control ${errors.name ? 'error' : ''}`}
                    placeholder="Tu nombre completo"
                  />
                  {errors.name && <div className="form-error">{errors.name}</div>}
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`form-control ${errors.email ? 'error' : ''}`}
                    placeholder="tu@email.com"
                  />
                  {errors.email && <div className="form-error">{errors.email}</div>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Teléfono</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="Tu número de teléfono"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="birthdate">Fecha de nacimiento</label>
                  <input
                    type="date"
                    id="birthdate"
                    name="birthdate"
                    value={formData.birthdate}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="bio">Biografía</label>
                <textarea
                  id="bio"
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  className="form-control"
                  rows="3"
                  placeholder="Cuéntanos un poco sobre ti..."
                />
              </div>
            </div>

            {/* Sección de Dirección */}
            <div className="profile-section">
              <h3 className="section-title">
                <span className="material-icons">home</span>
                Dirección
              </h3>

              <div className="form-group">
                <label htmlFor="address">Dirección</label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Calle y número"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="city">Ciudad</label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="Tu ciudad"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="postalCode">Código Postal</label>
                  <input
                    type="text"
                    id="postalCode"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="12345"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="country">País</label>
                  <input
                    type="text"
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="España"
                  />
                </div>
              </div>
            </div>

            {/* Sección de Redes Sociales */}
            <div className="profile-section">
              <h3 className="section-title">
                <span className="material-icons">share</span>
                Redes Sociales
              </h3>

              <div className="form-group">
                <label htmlFor="personalInstagram">Tu Instagram personal</label>
                <input
                  type="text"
                  id="personalInstagram"
                  name="personalInstagram"
                  value={formData.personalInstagram}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="@tu_usuario_instagram"
                />
              </div>
            </div>
          </div>
        </form>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={loading}
          >
            Cancelar
          </button>

          <button
            type="submit"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Guardando...' : 'Guardar Cambios'}
          </button>
        </div>

        {errors.submit && (
          <div className="form-error" style={{ textAlign: 'center', marginTop: '10px' }}>
            {errors.submit}
          </div>
        )}
      </div>
    </div>
  );
};

TutorProfileEditModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
};

export default TutorProfileEditModal;
